import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import { useQuery } from "@tanstack/react-query";
import { CircleCheck, Hand, Hourglass, Mailbox, Radar, ThumbsUp } from "lucide-mui";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Area, AreaChart, ResponsiveContainer } from "recharts";
import { api, type OrgDashboard } from "../api/client.ts";
import { keys } from "../api/queryKeys.ts";
import { ORG_ADMIN_SCOPE } from "../auth/orgRoutes.ts";
import { userHasOrgScope } from "../auth/scopes.ts";
import AgentOutputPanel from "../components/dashboard/org/AgentOutputPanel.tsx";
import { formatDuration } from "../components/dashboard/org/agentActivity.tsx";
import OnboardingPanel from "../components/dashboard/org/OnboardingPanel.tsx";
import OpenOrdersPanel from "../components/dashboard/org/OpenOrdersPanel.tsx";
import RecentListsPanel from "../components/dashboard/org/RecentListsPanel.tsx";
import RecentWorkPanel from "../components/dashboard/org/RecentWorkPanel.tsx";
import TenantCards from "../components/dashboard/org/TenantCards.tsx";
import WorkingNowPanel from "../components/dashboard/org/WorkingNowPanel.tsx";
import EmptyTenantPrompt from "../components/dashboard/EmptyTenantPrompt.tsx";
import FilterBar from "../components/filters/FilterBar.tsx";
import type { FilterSpec } from "../components/filters/specs.ts";
import { useFilters } from "../components/filters/useFilters.ts";
import KpiTile from "../components/KpiTile.tsx";
import NoSelectionPrompt from "../components/NoSelectionPrompt.tsx";
import PageHeader from "../components/PageHeader.tsx";
import { HIT_KIND_COLORS, hitTimelinePoints } from "../components/prospects/hitTimeline.ts";
import { useAuth } from "../contexts/AuthContext.tsx";
import { useOrganization } from "../contexts/OrganizationContext.tsx";
import { collectionPath, useTenant } from "../contexts/TenantContext.tsx";
import { useUrlState } from "../hooks/useUrlState.ts";
import { formatNumber, percent } from "../utils/format.ts";
import { periodFrom, periodLabel, periodSpec, sinceFor } from "../utils/periods.ts";

/**
 * The organization's home: how far its tenants are from agents prospecting
 * for them, what the agents are doing right now, what they found and what is
 * waiting for a person. Every count covers the org's tenants together.
 */
function OrganizationDashboardPage() {
  const { selectedOrgId, currentOrg } = useOrganization();
  const { tenants, isLoading: tenantsLoading, tenantPath } = useTenant();
  const { user } = useAuth();
  const url = useUrlState();
  const period = periodFrom(url.searchParams.get("period"));
  const since = sinceFor(period);
  const specs = useMemo<FilterSpec[]>(() => [periodSpec()], []);
  const filters = useFilters(specs, { state: url });
  const hasNoTenants = !tenantsLoading && tenants.length === 0;

  const { data } = useQuery({
    queryKey: keys.organization.dashboard(selectedOrgId, since ?? null),
    queryFn: () => api.getOrgDashboard(selectedOrgId!, since),
    enabled: !!selectedOrgId && !hasNoTenants,
    // Pushed (at most every 10 s), with each live run's current step.
    meta: { live: true },
  });

  const orgName = currentOrg?.name ?? "your organization";
  const single = tenants.length === 1 ? tenants[0] : null;
  const description = single
    ? `What the agents are doing for ${single.name}, and what is waiting for you`
    : `Prospecting across ${orgName}'s ${tenants.length} tenants`;

  return (
    <>
      <PageHeader
        title="Dashboard"
        description={selectedOrgId && !hasNoTenants ? description : `Prospecting overview for ${orgName}`}
        actions={
          selectedOrgId && !hasNoTenants ? (
            <Button component={Link} to={tenantPath("/prospects/inbox")} variant="contained" startIcon={<Mailbox />}>
              Open inbox
            </Button>
          ) : undefined
        }
      />

      {selectedOrgId === null ? (
        <NoSelectionPrompt kind="org" />
      ) : hasNoTenants ? (
        <EmptyTenantPrompt />
      ) : (
        <>
          <Box sx={{ mb: 2 }}>
            <FilterBar filters={filters} variant="inline" />
          </Box>
          {data ? (
            <DashboardBody
              data={data}
              since={since}
              periodName={periodLabel(period).toLowerCase()}
              canManageOrg={userHasOrgScope(user, selectedOrgId, ORG_ADMIN_SCOPE)}
              inboxTo={single ? tenantPath("/prospects/inbox") : undefined}
              allListsTo={tenantPath(collectionPath())}
            />
          ) : (
            <DashboardSkeleton />
          )}
        </>
      )}
    </>
  );
}

interface DashboardBodyProps {
  data: OrgDashboard;
  since: string | undefined;
  periodName: string;
  canManageOrg: boolean;
  inboxTo: string | undefined;
  allListsTo: string;
}

function DashboardBody({ data, since, periodName, canManageOrg, inboxTo, allListsTo }: DashboardBodyProps) {
  const tenantsById = useMemo(() => new Map(data.tenants.map((t) => [t.id, t])), [data.tenants]);
  const many = data.tenants.length > 1;
  const sum = (pick: (t: OrgDashboard["tenants"][number]) => number) => data.tenants.reduce((n, t) => n + pick(t), 0);
  const inbox = sum((t) => t.inbox);
  const worklist = sum((t) => t.worklist);
  const kind = (k: "signal" | "agent" | "manual") => data.by_kind.find((b) => b.kind === k);
  const found = (kind("agent")?.prospects ?? 0) + (kind("signal")?.prospects ?? 0);
  const manual = kind("manual")?.prospects ?? 0;
  const decided = data.decisions.positive + data.decisions.negative;
  const seconds = data.agents.reduce((n, a) => n + a.research_seconds, 0);
  const runs = data.agents.reduce((n, a) => n + a.runs, 0);
  const failed = data.agents.reduce((n, a) => n + a.failed, 0);
  const points = hitTimelinePoints(data.timeline, data.timeline_bucket, since);
  const automated = points.map((p) => ({ t: p.t, v: p.signal + p.agent }));

  return (
    <Stack spacing={2.5}>
      <OnboardingPanel tenants={data.tenants} canManageOrg={canManageOrg} />

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <KpiTile
            label="Needs a decision"
            icon={inbox ? <Hand /> : <CircleCheck />}
            color={inbox ? "warning" : "success"}
            value={inbox}
            caption={inbox ? `${formatNumber(worklist)} being pursued` : "Inbox zero"}
            to={inboxTo}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <KpiTile
            label={`Found by agents · ${periodName}`}
            icon={<Radar />}
            value={found}
            caption={`${found === 1 ? "company" : "companies"} from signals and agents${manual ? `, ${formatNumber(manual)} added by hand` : ""}`}
          >
            {automated.length > 1 && found > 0 && (
              <Box sx={{ height: 36, mx: -0.5 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={automated} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
                    <Area
                      type="monotone"
                      dataKey="v"
                      stroke={HIT_KIND_COLORS.agent}
                      fill={HIT_KIND_COLORS.agent}
                      fillOpacity={0.15}
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            )}
          </KpiTile>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <KpiTile
            label="Accepted by your team"
            icon={<ThumbsUp />}
            color="success"
            value={percent(data.decisions.positive, decided)}
            caption={decided ? `of ${formatNumber(decided)} decisions on agent work` : "No decisions in this period"}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <KpiTile
            label="Agent research time"
            icon={<Hourglass />}
            value={formatDuration(seconds)}
            caption={runs ? `${formatNumber(runs)} ${runs === 1 ? "run" : "runs"}${failed ? ` · ${formatNumber(failed)} failed` : ""}` : "No runs in this period"}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, md: 5 }}>
          <WorkingNowPanel live={data.live} lastFinished={data.recent[0]} tenants={tenantsById} showTenant={many} />
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <AgentOutputPanel points={points} bucket={data.timeline_bucket} agents={data.agents} />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, md: 7 }}>
          <RecentWorkPanel runs={data.recent} tenants={tenantsById} showTenant={many} />
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <Stack spacing={2.5}>
            <OpenOrdersPanel orders={data.orders} tenants={tenantsById} showTenant={many} />
            <RecentListsPanel tenants={data.tenants} allListsTo={allListsTo} />
          </Stack>
        </Grid>
      </Grid>

      {many && <TenantCards tenants={data.tenants} />}
    </Stack>
  );
}

function DashboardSkeleton() {
  return (
    <Stack spacing={2.5}>
      <Grid container spacing={2.5}>
        {[0, 1, 2, 3].map((i) => (
          <Grid key={i} size={{ xs: 12, sm: 6, lg: 3 }}>
            <Skeleton variant="rounded" height={132} />
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Skeleton variant="rounded" height={320} />
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <Skeleton variant="rounded" height={320} />
        </Grid>
      </Grid>
    </Stack>
  );
}

export default OrganizationDashboardPage;
